/**
 * MasterWelcomePage — B-1. 사장님 웰컴
 *
 * 마스터 신규 가입 후 studioStage === 0 일 때 AppFrame ContentGuard → /master-welcome
 * [스튜디오 시작하기] 클릭 → PATCH /studio/stage (1) → appStore 업데이트 → /app
 *
 * AppFrame 밖(RequireAuth 하위) 독립 full-page — LeftNav/TopBar 없음.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@indipia/shared';
import { setupAgents, updateStudioStage, updateCompletion } from '../api/studio';

export function MasterWelcomePage() {
  const navigate      = useNavigate();
  const queryClient   = useQueryClient();
  const session       = useAppStore((s) => s.session);
  const setStudioStage = useAppStore((s) => s.setStudioStage);
  const [loading, setLoading] = useState(false);

  const handleStart = async () => {
    setLoading(true);
    try {
      // [1집 기준] 스튜디오 시작 시 에이전트 1·2·8 자동 구독 + stage 1 승격 + 완성도 반영
      // 추후 유료 전환 시 이 블록 제거 — 구독은 별도 구독 플로우로 분리
      await setupAgents([1, 2, 8]);
      await updateStudioStage(1);
      // 완성도 업데이트는 실패해도 진입을 막지 않음
      updateCompletion('AGENT_SUB', 'UP').catch(console.error);

      // navigate 전에 init 캐시를 최신값으로 교체 — AppFrame 재마운트 시 stale 캐시가
      // store를 덮어써 studioStage=0으로 되돌리는 루프를 방지
      await queryClient.refetchQueries({ queryKey: ['app-init'] });
      // store도 즉시 반영 — refetch 완료 후 setInit이 이미 1로 써주지만 명시적으로 보장
      setStudioStage(1);
      navigate('/app', { replace: true });
    } catch {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes b1fu{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes b1si{from{opacity:0;transform:scale(.55)}to{opacity:1;transform:scale(1)}}
        @keyframes b1dc{to{stroke-dashoffset:0}}
        @keyframes b1dck{to{stroke-dashoffset:0}}
        .b1-brand{animation:b1fu .5s ease both}
        .b1-icon{animation:b1si .5s cubic-bezier(.34,1.56,.64,1) .15s both}
        .b1-c-line{stroke-dashoffset:295;animation:b1dc .8s ease .4s forwards}
        .b1-ck{stroke-dashoffset:62;animation:b1dck .35s ease 1.15s forwards}
        .b1-h1{animation:b1fu .6s ease 1s both}
        .b1-sub{animation:b1fu .6s ease 1.15s both}
        .b1-cta{transition:background .15s,box-shadow .15s,transform .12s}
        .b1-cta:not(:disabled):hover{background:var(--warm-peach-600) !important;box-shadow:0 12px 40px color-mix(in srgb, var(--warm-peach-500) 38%, transparent) !important}
        .b1-cta:not(:disabled):active{transform:scale(.97)}
        .b1-cta-wrap{animation:b1fu .6s ease 1.3s both}
      `}</style>

      <div className="relative flex min-h-screen flex-col items-center justify-between overflow-hidden bg-surface px-6 pb-11 pt-14">

        {/* 배경 glow — 오렌지 */}
        <div
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2"
          style={{ width: 800, height: 480, background: 'radial-gradient(ellipse at 50% 0%,color-mix(in srgb, var(--warm-peach-500) 10%, transparent) 0%,transparent 65%)' }}
        />

        {/* Brand */}
        <div className="b1-brand z-10 flex items-center gap-2">
          <span className="text-[13px] font-extrabold uppercase tracking-[1.5px] text-point">INDIPIA</span>
          <span className="h-1 w-1 rounded-full bg-point opacity-50" />
          <span className="text-[10px] font-semibold uppercase tracking-[2px] text-text-muted">Pro</span>
        </div>

        {/* 중앙 콘텐츠 */}
        <div className="z-10 flex flex-1 flex-col items-center justify-center pb-6 text-center">

          {/* 완료 아이콘 — draw-in 원 + 체크 */}
          <div className="b1-icon relative mb-11 h-[100px] w-[100px]">
            <div
              className="absolute -inset-6 rounded-full"
              style={{ background: 'radial-gradient(ellipse at center,color-mix(in srgb, var(--warm-peach-500) 18%, transparent) 0%,transparent 65%)' }}
            />
            <svg className="relative z-10" width="100" height="100" viewBox="0 0 100 100">
              {/* 트랙 */}
              <circle
                cx="50" cy="50" r="47"
                fill="var(--warm-peach-500)" fillOpacity=".06"
                stroke="var(--warm-peach-500)" strokeOpacity=".15"
                strokeWidth="2"
              />
              {/* draw-in 원 */}
              <circle
                className="b1-c-line"
                cx="50" cy="50" r="47"
                fill="none"
                stroke="var(--warm-peach-500)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray="295"
                transform="rotate(-90 50 50)"
              />
              {/* 체크마크 */}
              <polyline
                className="b1-ck"
                points="30,52 44,66 70,36"
                fill="none"
                stroke="var(--warm-peach-500)"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="62"
              />
            </svg>
          </div>

          {/* 헤드라인 */}
          <h1
            className="b1-h1 mb-[18px] text-[48px] font-black leading-[1.12] text-text"
            style={{ letterSpacing: '-2.2px', wordBreak: 'keep-all' }}
          >
            <span className="text-point">{session?.memberName ?? '사장님'}</span> 사장님,<br />
            환영해요!
          </h1>

          {/* 서브텍스트 */}
          <p
            className="b1-sub mb-[52px] max-w-[420px] text-[15px] leading-[1.85] text-text-muted"
            style={{ letterSpacing: '-.2px', wordBreak: 'keep-all' }}
          >
            사장님만의 스튜디오가 만들어졌어요<br />
            에이전트 3개를 담아서 스튜디오를 시작합니다.
          </p>

          {/* CTA */}
          <div className="b1-cta-wrap">
            <button
              type="button"
              disabled={loading}
              onClick={handleStart}
              className="b1-cta inline-flex items-center gap-2.5 rounded-xl bg-point px-[52px] py-[18px] text-base font-bold text-white disabled:opacity-70"
              style={{ boxShadow: '0 8px 28px color-mix(in srgb, var(--warm-peach-500) 28%, transparent)', letterSpacing: '-.3px' }}
            >
              {loading ? '이동 중...' : '스튜디오 시작하기'}
              {!loading && <span className="text-lg">→</span>}
            </button>
          </div>
        </div>

      </div>
    </>
  );
}
